import React, { useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { useScenarioStore } from "scenarios/ScenarioContext";
import _ from "lodash";
import styles from "./_css/textSectionEditor.module.css";
import "react-quill/dist/quill.snow.css";
import { TEXT_SIZES } from "scenarios/_models/ScenarioMdl";
import { MenuItem } from "@material-ui/core";
import { UiSelect } from "common/ui/mui/forms/UiSelect";
import { useTranslation } from "react-i18next";

type Props = {
    text: string;
    id: string;
    componentId?: string;
    styles?: React.CSSProperties;
    isEditable?: boolean;
    onEdit?: () => void;
    textSize?: "small" | "medium" | "large";
};

export const modules = {
    toolbar: [
        [{ font: [] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
        [{ align: [] }],
        ["clean"],
    ],
};

export const formats = [
    "font",
    "size",
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "align",
];

export function TextQuillSectionEditor(props: Props) {
    const { t } = useTranslation();
    const { scenarioStore } = useScenarioStore();

    const [ReactQuill, setReactQuill] = useState<any>(null);
    const [currentEvent, setCurrentEvent] = useState(props);
    const quillRef = useRef<any>(null);

    useEffect(() => {
        import("react-quill").then((mod) => {
            setReactQuill(() => mod.default);
        });
    }, []);

    useEffect(() => {
        if (!ReactQuill) return;
        const editor = quillRef.current?.getEditor?.();
        if (!editor) return;

        const editorElem = quillRef.current?.editor?.root || editor.root;
        if (!editorElem) return;

        editorElem.classList.remove(styles.textSmall, styles.textMedium, styles.textLarge);

        if (props.textSize === "small") editorElem.classList.add(styles.textSmall);
        else if (props.textSize === "medium") editorElem.classList.add(styles.textMedium);
        else if (props.textSize === "large") editorElem.classList.add(styles.textLarge);
    }, [props.textSize, ReactQuill]);

    if (!ReactQuill) {
        return (
            <textarea
                className={clsx(styles.textarea, {
                    [styles.textSmall]: props.textSize === "small",
                    [styles.textMedium]: props.textSize === "medium",
                    [styles.textLarge]: props.textSize === "large",
                })}
                style={{ ...props.styles }}
                rows={10}
                value={props.text}
                onChange={(e) => {
                    scenarioStore.editSection(props.id, {
                        props: {
                            ..._.omit(props, ["onEdit", "isEditable", "componentId"]),
                            text: e.target.value,
                            textSize: currentEvent.textSize ?? TEXT_SIZES.MEDIUM,
                        },
                    });
                    if (props.onEdit) props.onEdit();
                }}
                readOnly={!props.isEditable}
            />
        );
    }

    return (
        <div
            style={{ ...props.styles }}
            className={"ph_20 pv_10"}
            onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
            }}
        >
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={props.text}
                onChange={(content: string) => {
                    scenarioStore.editSection(props.id, {
                        props: {
                            ..._.omit(props, ["onEdit", "isEditable", "componentId"]),
                            text: content,
                            textSize: currentEvent.textSize ?? TEXT_SIZES.MEDIUM,
                        },
                    });
                    if (props.onEdit) props.onEdit();
                }}
                readOnly={!props.isEditable}
                className={clsx({
                    [styles.textSmall]: props.textSize === TEXT_SIZES.SMALL,
                    [styles.textMedium]: props.textSize === TEXT_SIZES.MEDIUM,
                    [styles.textLarge]: props.textSize === TEXT_SIZES.LARGE,
                })}
                modules={modules}
                formats={formats}
            />

            <UiSelect
                value={currentEvent?.textSize ?? TEXT_SIZES.MEDIUM}
                displayEmpty
                className={"mt_5"}
                onChange={(e) => {
                    const newSize = e.target.value as typeof TEXT_SIZES[keyof typeof TEXT_SIZES];
                    setCurrentEvent({
                        ...props,
                        textSize: newSize,
                    });

                    scenarioStore.editSection(props.id, {
                        props: {
                            ..._.omit(props, ["onEdit", "isEditable", "componentId"]),
                            text: props.text,
                            textSize: newSize,
                        },
                    });

                    if (props.onEdit) props.onEdit();
                }}
            >
                {Object.values(TEXT_SIZES).map((size) => (
                    <MenuItem key={size} value={size}>
                        {t(`editor.textSize.${size}`)}
                    </MenuItem>
                ))}
            </UiSelect>
        </div>
    );
}
